import { useLayoutEffect, useRef, useState } from "react";
import cn from "classnames";
import { FCX } from "@datassential/platform-ui-lib";
import { ARROW_SIZE, ViewConfig } from "./PopularityTrendGapChart.constants";
import { computeTooltipPosition, PlottableKeyword, TooltipPosition } from "./PopularityTrendGapChart.helpers";
import PopularityTrendGapTooltip from "./PopularityTrendGapTooltip";

export interface ActiveDot {
    kw: PlottableKeyword;
    cx: number;
    cy: number;
}

interface Props {
    activeDot:  ActiveDot | null;
    config:     ViewConfig;
    containerW: number;
    containerH: number;
}

/**
 * Dot-anchored tooltip overlay. Must be rendered as a descendant of the same
 * position:relative element the SVG plot sits in, with no padding/border between
 * them, so this component's absolute left/top map directly onto the SVG's cx/cy.
 *
 * Measures its own rendered size via ref + useLayoutEffect (getBoundingClientRect),
 * then clamps its position fully inside the plot container via computeTooltipPosition
 * so it never covers controls above the chart or bleeds past the plot's edges. A caret
 * on the box points back at the dot: when the box sits above the dot the caret is on
 * the box's bottom edge pointing down; when edge-clamping flips the box below the dot,
 * the caret moves to the top edge pointing up. Stays visibility:hidden until the first
 * measurement to avoid a flash at (0,0), and pointer-events:none so it can never itself
 * trigger a dot's mouseleave.
 */
const PopularityTrendGapHoverTooltip: FCX<Props> = ({
    activeDot,
    config,
    containerW,
    containerH,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<TooltipPosition | null>(null);

    useLayoutEffect(() => {
        if (!activeDot || !ref.current) {
            setPosition(null);
            return;
        }
        const { width: tipW, height: tipH } = ref.current.getBoundingClientRect();
        setPosition(
            computeTooltipPosition({
                cx: activeDot.cx,
                cy: activeDot.cy,
                tipW,
                tipH,
                containerW,
                containerH,
            }),
        );
    }, [activeDot, containerW, containerH]);

    if (!activeDot) return null;

    // A box placed above the dot needs its caret pointing down (on its bottom edge);
    // a box flipped below the dot needs its caret pointing up (on its top edge).
    const arrowDirection = position?.placement === 'bottom' ? 'up' : 'down';

    return (
        <div
            ref={ref}
            role="tooltip"
            data-placement={position?.placement ?? 'top'}
            className="PopularityTrendGapChart__tooltip-overlay"
            style={{
                position: 'absolute',
                left: position?.left ?? 0,
                top: position?.top ?? 0,
                visibility: position ? 'visible' : 'hidden',
                pointerEvents: 'none',
            }}
        >
            <PopularityTrendGapTooltip keyword={activeDot.kw} config={config} />
            <span
                data-testid="tooltipArrow"
                className={cn(
                    'PopularityTrendGapChart__tooltip-arrow',
                    `PopularityTrendGapChart__tooltip-arrow--${arrowDirection}`,
                )}
                style={{ left: (position?.arrowLeft ?? 0) - ARROW_SIZE }}
            />
        </div>
    );
};

export default PopularityTrendGapHoverTooltip;
